import { useEffect, useState } from 'react';
import { Home, X } from 'lucide-react';
import { AxisFields, AxisLetters, AxisRowTitle, MM_PER_CM, asCm, asNumber } from './axisGrid';
import { useLanguage } from '../../contexts/LanguageContext';

// WHERE THE SELECTED PART IS, AND WHERE IT SHOULD BE — the panel a click on a part opens.
//
// Six numbers, in the assembly's own frame: the frame the gizmo draws at the origin and the
// frame every other field on this page is written in. Three of them are the part's CENTRE,
// which is also its origin — every mesh is re-origined to the middle of its own box when it
// loads, so there is one point to name and not two — and three are how it is turned where it
// seats, as XYZ Euler degrees.
//
// IT IS A LIVE READING AND AN INSTRUCTION AT ONCE. The fields follow the part as it plays,
// because they say where the part is; typing in one changes
// nothing until you leave it or press Enter, because a half-typed number is not a position
// anybody meant. That is also why the fields are text rather than number inputs: a number
// spinner applies as you touch it.
//
// THERE IS NO APPLY BUTTON, and there was. It said "Move here" under the boxes, and it was the
// only thing on the page claiming that a typed number is a proposal — leaving a field is
// already the moment you have finished with it, and the button made you say so twice. What it
// really bought was a place to hesitate, and Escape-to-revert is the honest version of that.
//
// AND IT IS DRAWN IN THE SAME GRID AS THE VIEWPORT CORNER (see axisGrid), because it answers
// the same two questions the corner answers about the camera. Letters over columns, a titled
// row per reading, the unit said once in the title. Read one and you can read the other.

export type PartCoords = {
  instanceId: string;
  // Live, in millimetres, in the assembly's frame.
  centre: [number, number, number];
  // AND HOW IT IS TURNED WHERE IT SEATS: XYZ Euler degrees, same frame, same three colours.
  //
  // A part comes out of the Parts DB in whatever orientation it was reconstructed in, and
  // until this existed the only answer to "this leg is lying on its side" was to go and fix
  // the mesh. It is not the GESTURE — that is the rotation a placing arrives through and
  // unwinds to nothing by the time it seats — and it is not the thread's roll, which is a
  // function of the travel. This is where the part RESTS.
  angles: [number, number, number];
};

const ZEROES: [string, string, string] = ['0', '0', '0'];

export function PartCoordsPanel({ pick, onValidate, onClose }: {
  pick: PartCoords;
  // The position and the orientation typed in, in the same frame and units the panel shows —
  // millimetres and degrees. Both go together on one call: they are one answer about where a
  // part belongs, and sending half of it would be a part that moved without turning.
  onValidate: (to: [number, number, number], turnedTo: [number, number, number]) => void;
  onClose: () => void;
}) {
  const { t: tr } = useLanguage();
  // WHAT IS IN THE BOXES, which is not the same thing as where the part is. It follows the
  // part while it plays and while another one is picked, and it stops
  // following the moment somebody types — up to the commit, the typed number is theirs.
  const [typed, setTyped] = useState<string[]>(['0', '0', '0']);
  const [spun, setSpun] = useState<string[]>(['0', '0', '0']);
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    if (edited) return;
    setTyped(pick.centre.map((v) => String(asCm(v))));
    // Degrees are already the unit they are read in, so this one only rounds.
    setSpun(pick.angles.map((v) => String(Math.round(v * 100) / 100)));
  }, [pick.centre, pick.angles, pick.instanceId, edited]);

  // A different part is a different question, so whatever was half-typed about the last one
  // goes with it.
  useEffect(() => { setEdited(false); }, [pick.instanceId]);

  // WHAT IS IN THE BOXES, SENT. Takes them as arguments rather than reading the state, because
  // the origin button below sets and commits in one go — and a `setTyped` followed by a
  // `commit()` would send the state as it was one render ago, which is the part's old place.
  const commit = (
    at: string[] = typed,
    turned: string[] = spun,
  ) => {
    // Back into millimetres, which is what every other number about this fitting is in.
    const to = at.map((v) => asNumber(v) * MM_PER_CM) as [number, number, number];
    const turnedTo = turned.map(asNumber) as [number, number, number];
    onValidate(to, turnedTo);
    setEdited(false);
  };

  // WHAT WAS IN THEM BEFORE ANYBODY TYPED, put back. Escape is the way out of a number you
  // have started and thought better of, which matters more now that leaving a field commits
  // it: without this the only way to undo a typo would be to remember what it replaced.
  const revert = () => {
    setEdited(false);
    setTyped(pick.centre.map((v) => String(asCm(v))));
    setSpun(pick.angles.map((v) => String(Math.round(v * 100) / 100)));
  };

  // Three columns of equal width, sharing whatever the card is wide — `min-w-0` because a
  // text input's default width is wider than a third of this panel and would otherwise push
  // the grid past its edge.
  const cell = 'min-w-0 flex-1 text-center';

  // The fields themselves are the grid's — see AxisFields, which the viewport corner now
  // draws its two rows with as well. What is left here is the only part that is this panel's
  // own: that typing suspends the live reading, and that both rows go up together.
  const row = (
    values: string[],
    set: (next: string[]) => void,
    commitWith: (next: string[]) => void,
  ) => (
    <AxisFields
      values={values}
      cell={cell}
      onChange={(next) => { setEdited(true); set(next); }}
      onCommit={commitWith}
      onRevert={revert}
    />
  );

  return (
    // INSIDE THE PARTS LIST, under the row of the part it is about — see ScenePartCards.
    <div className="w-full border-t border-slate-200 bg-white p-2">
      {/* THE TWO BUTTONS THAT ARE ABOUT THE PANEL RATHER THAN IN IT, on a row of their own
          above the grid. Lifted out of the flow they sat over the letters — and a close box
          on top of the Z column is a click you have to aim. */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => { setTyped(ZEROES); setSpun(ZEROES); commit(ZEROES, ZEROES); }}
          // Keeps the focused field from blurring first, which would send whatever is in it
          // a moment before this sends the zeroes — two writes to say one thing.
          onMouseDown={(e) => e.preventDefault()}
          title={tr('notice.assembly.coordsOrigin')}
          aria-label={tr('notice.assembly.coordsOrigin')}
          className="-ml-0.5 rounded p-0.5 text-slate-400 transition-colors hover:text-blue-600"
        >
          <Home className="h-3 w-3" />
        </button>
        {/* THE SAME HOUSE THE VIEWPORT CORNER USES, and deliberately: there it puts the
            CAMERA back to a known place, here it puts the PART there. One icon, one meaning
            — go to the reference — and the reference is the origin the gizmo stands at in
            both cases. All six at once, because a part half-returned is neither where it was
            nor where you sent it. */}
        <button
          type="button"
          onClick={onClose}
          className="-mr-0.5 rounded p-0.5 text-slate-400 transition-colors hover:text-slate-700"
          aria-label={tr('notice.assembly.coordsClose')}
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      <AxisLetters cell={cell} />
      <AxisRowTitle>{tr('notice.assembly.coords')}</AxisRowTitle>
      {row(typed, setTyped, (next) => commit(next, spun))}
      <AxisRowTitle>{tr('notice.assembly.orientation')}</AxisRowTitle>
      {row(spun, setSpun, (next) => commit(typed, next))}
    </div>
  );
}
